import { useEffect, useRef, useState, type JSX } from 'react';

/**
 * Mirror of `@peacock/sdk-astro/preview-bridge` message types — kept
 * inline so the admin doesn't need to depend on the Astro SDK
 * (which has astro itself as a peer). Phase 7's npm-release pipeline
 * will extract a shared `@peacock/preview-protocol` package.
 */
type PreviewMessageFromEditor =
  | { type: 'peacock:update'; storyUuid: string; content: Record<string, unknown> }
  | { type: 'peacock:highlight'; path: string }
  | { type: 'peacock:scroll-to'; path: string };

type PreviewMessageFromIframe =
  | { type: 'peacock:ready'; url: string }
  | { type: 'peacock:click'; path: string }
  | { type: 'peacock:hover'; path: string };

/**
 * Phase 3 increment E — the right-hand iframe in the editor.
 *
 * Embeds the customer's Astro preview URL inside an `<iframe>`, and
 * speaks the postMessage protocol defined in `@peacock/sdk-astro/preview`.
 *
 * Trust boundary: the iframe origin is configurable per-Space and is
 * almost always a different origin from the admin. We MUST validate
 * every incoming message's origin against the configured iframe URL.
 * Without this, a malicious page loaded in any browser tab could
 * forge `peacock:click` events at the editor and trick the editor
 * into selecting a different block.
 */

export interface PreviewPaneProps {
  /**
   * URL the iframe loads. Typically the customer's preview origin
   * with a query flag, e.g. `https://staging.example.com/about?peacock-preview=1`.
   * `null` renders a placeholder.
   */
  previewUrl: string | null;
  /**
   * Story content to push into the iframe whenever it changes. The
   * iframe's mountPreview() listener applies it without a full reload.
   */
  content: Record<string, unknown> | null;
  /** Story UUID — included in the update message so the receiver can sanity-check. */
  storyUuid: string | null;
  /** Called when the user clicks a block inside the iframe (path = JSON-Pointer-ish). */
  onBlockClick?(path: string): void;
}

export function PreviewPane({
  previewUrl,
  content,
  storyUuid,
  onBlockClick,
}: PreviewPaneProps): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeReady, setIframeReady] = useState(false);

  // Compute the origin we trust messages from. Defensive: a
  // malformed previewUrl would otherwise let URL() throw mid-render.
  const trustedOrigin = (() => {
    if (previewUrl === null) return null;
    try {
      return new URL(previewUrl).origin;
    } catch {
      return null;
    }
  })();

  // Listen for messages from the iframe.
  useEffect(() => {
    if (trustedOrigin === null) return undefined;

    function handleMessage(event: MessageEvent): void {
      // Origin check — see class docblock. Locks in the security
      // contract that's mirrored on the Astro side in mountPreview().
      if (event.origin !== trustedOrigin) {
        return;
      }
      const data = event.data as Partial<PreviewMessageFromIframe> | null;
      if (data === null || typeof data !== 'object' || typeof data.type !== 'string') {
        return;
      }
      switch (data.type) {
        case 'peacock:ready':
          setIframeReady(true);
          return;
        case 'peacock:click':
          if (typeof data.path === 'string') {
            onBlockClick?.(data.path);
          }
          return;
        case 'peacock:hover':
          // Phase-3-E doesn't render hover-highlights in the editor;
          // increment F will wire this to the BlockEditor's outline.
          return;
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [trustedOrigin, onBlockClick]);

  // Push updates whenever content or storyUuid changes, but ONLY
  // after the iframe announced readiness. Sending before
  // `peacock:ready` means the message is dropped silently — the
  // iframe hasn't installed its listener yet.
  useEffect(() => {
    if (!iframeReady || trustedOrigin === null || content === null || storyUuid === null) {
      return;
    }
    const message: PreviewMessageFromEditor = {
      type: 'peacock:update',
      storyUuid,
      content,
    };
    iframeRef.current?.contentWindow?.postMessage(message, trustedOrigin);
  }, [iframeReady, trustedOrigin, content, storyUuid]);

  // Reset the ready flag whenever the URL changes — the iframe will
  // re-announce after the new page finishes loading.
  useEffect(() => {
    setIframeReady(false);
    setReachable(null);
  }, [previewUrl]);

  // Connectivity probe — if the preview origin doesn't respond within
  // 4 seconds, render a friendly empty-state instead of an indefinite
  // Chromium "ERR_CONNECTION_REFUSED" page. The browser swallows
  // iframe connection errors (cross-origin), so without this probe
  // the user just sees the bare error chrome (E2E sweep 2026-05-30).
  // We use HEAD/no-cors so the probe never CORS-fails on a healthy
  // server that simply doesn't allow our origin.
  const [reachable, setReachable] = useState<boolean | null>(null);
  useEffect(() => {
    if (previewUrl === null) return undefined;
    let abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 4000);
    fetch(previewUrl, { method: 'HEAD', mode: 'no-cors', signal: abort.signal })
      .then(() => setReachable(true))
      .catch(() => setReachable(false))
      .finally(() => clearTimeout(timer));
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [previewUrl]);

  if (previewUrl === null) {
    return (
      <div className="grid h-full place-items-center p-6 text-center text-sm text-zinc-600">
        <div>
          <p className="font-medium">No preview URL configured</p>
          <p className="mt-2 text-xs text-zinc-500">
            Set a preview origin in your Space settings to live-render
            stories from the editor.
          </p>
        </div>
      </div>
    );
  }

  if (reachable === false) {
    return (
      <div className="grid h-full place-items-center p-6 text-center text-sm text-zinc-600">
        <div className="max-w-md">
          <p className="font-medium">Live-Vorschau nicht erreichbar</p>
          <p className="mt-2 text-xs text-zinc-500">
            <code className="rounded bg-zinc-100 px-1 py-0.5 font-mono">
              {previewUrl}
            </code>{' '}
            antwortet nicht. Starte deinen Frontend-Dev-Server oder
            ändere <code className="rounded bg-zinc-100 px-1 py-0.5 font-mono">VITE_PEACOCK_PREVIEW_BASE_URL</code>{' '}
            in <code className="rounded bg-zinc-100 px-1 py-0.5 font-mono">apps/admin/.env.development.local</code>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <iframe
        ref={iframeRef}
        src={previewUrl}
        title="Story-Vorschau"
        className="h-full w-full border-0"
        // Sandbox: same-origin so the iframe can `window.parent.postMessage`,
        // scripts so the page actually renders, forms so an editor
        // can submit example forms in preview. Allow-popups for
        // outbound links. Deliberately NOT `allow-top-navigation` —
        // a malicious customer page shouldn't be able to navigate the
        // admin away.
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
      />
      {!iframeReady ? (
        <div className="pointer-events-none absolute right-3 top-3 rounded-md bg-zinc-900/80 px-2 py-1 text-xs text-white shadow-sm">
          waiting for preview to connect…
        </div>
      ) : null}
    </div>
  );
}
